<script setup>
import { defineProps, ref, computed } from 'vue';
import TransactionBatchView from './TransactionBatchView.vue';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { dashboardAPI } from '../service/api.js';
import { formatCurrency } from '@/util/format'

const toast = useToast();
// Confirmation is rendered by the <ConfirmDialog /> already mounted in the
// parent BatchView.vue -- PrimeVue's ConfirmationService is a single global
// store, so a second <ConfirmDialog /> instance here would just duplicate
// the same dialog rather than being needed for this to work.
const confirm = useConfirm();

const statuses = ref({
    UNPROCESSED: { value: 'Unprocessed', order: 1},
    SUBMITTED: { value: 'Submitted', order: 2},
    PROCESSED: { value: 'Processed', order: 3}
});

const statusOptions = computed(() => {
    return Object.entries(statuses.value).sort(([, a], [, b]) => a.order - b.order).map(([key, value]) => ({name: value.value, code: key,}));
});

const expandedRows = ref({});
const transactionBatchesMap = ref({});
const previewVisible = ref(false);
const previewData = ref([]);
const previewColumns = ref([]);

const formatPaymentType = (batchType) => {
    switch (batchType) {
        case 'A':
            return 'ACH';
        case 'C':
            return 'CC';
        default:
            return '';
    }
}

const filters = ref({
     global: { value: null, matchMode: 'contains' },
     status: { value: null, matchMode: 'contains' }
});

const setStatusFilter = (value) => {
    filters.value.status.value = value.target.value;
};

const fetchTransactionBatches = async (bankPaymentFileId) => {
    try {
        const response = await dashboardAPI.getTransactionBatchesByBankPaymentFileId(bankPaymentFileId);
        return response.data; // Adjust based on the API response structure
    } catch (error) {
        console.error('Error fetching transaction batches:', error);
    }
}

const onExpand = async (event) => {
    const bankPaymentFileId = event.data.id

    const transactionBatches = await fetchTransactionBatches(bankPaymentFileId)
    if (transactionBatches) {
        transactionBatchesMap.value[bankPaymentFileId] = transactionBatches;
    }
}

const props = defineProps({
    bankPaymentFiles: {
        type: Array,
        required: true,
    },
});

const updateValue = async (data, field, value) => {
    const id = data.id;
    try {
        await dashboardAPI.updateBankPaymentFile(id, field, value);
    } catch (error) {
        const detail = error.response?.data || 'Failed to update Bank Payment File';
        toast.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
        console.error('Error updating bank payment file:', error);
        return;
    }
    const bpf = props.bankPaymentFiles.find(bpf => bpf.id === id);
    if (!bpf) {
        console.error('Bank Payment file not found:', id);
        return;
    }
    bpf[field] = value;
}

const downloadPaymentFile = (bankPaymentFileId, preview = false) => {
    dashboardAPI.downloadPaymentFile(bankPaymentFileId, preview).then((response) => {
        if (preview) {
            // Handle preview mode - show data in modal
            const data = response.data;
            if (Array.isArray(data) && data.length > 0) {
                // Generate columns from the first row keys
                previewColumns.value = Object.keys(data[0]).map(key => ({
                    field: key,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
                }));
                previewData.value = data;
                previewVisible.value = true;
            } else {
                toast.add({ severity: 'warn', summary: 'No Data', detail: 'No preview data available', life: 3000 });
            }
        } else {
            // Handle download mode - existing logic
            const disposition = response.headers['content-disposition'];
            let filename = 'payment.csv';

            if (disposition && disposition.indexOf('attachment') !== -1) {
                const matches = disposition.match(/filename="?(.+)"?/);
                if (matches[1]) {
                    filename = matches[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const bankPaymentFile = props.bankPaymentFiles.find(bpf => bpf.id === bankPaymentFileId);
            if (bankPaymentFile) {
                updateValue(bankPaymentFile, 'status', 'PROCESSED');
            }
        }
    })
    .catch((error) => {
        console.error('Error with payment file operation', error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to process payment file', life: 3000 });
    });
}

const pushInProgress = ref({});

const confirmPushToBank = (bankPaymentFile) => {
    confirm.require({
        message: 'Push this payment file to the bank over SFTP? This submits it for real -- it cannot be undone from here.',
        header: 'Push to Bank',
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        rejectLabel: 'Cancel',
        acceptLabel: 'Push',
        acceptClass: 'p-button-danger',
        accept: async () => {
            const id = bankPaymentFile.id;
            pushInProgress.value[id] = true;
            try {
                await dashboardAPI.pushBankPaymentFile(id);
                bankPaymentFile.status = 'SUBMITTED';
                toast.add({ severity: 'success', summary: 'Pushed to Bank', detail: 'Payment file was submitted over SFTP.', life: 3000 });
            } catch (error) {
                const detail = error.response?.data || 'Failed to push payment file to the bank';
                toast.add({ severity: 'error', summary: 'Push Failed', detail, life: 5000 });
                console.error('Error pushing bank payment file:', error);
            } finally {
                pushInProgress.value[id] = false;
            }
        },
    });
}
</script>

<template>
    <div>
        <DataTable
            size="medium"
            :filters="filters"
            filterDisplay="row"
            :expandedRows="expandedRows"
            :value="bankPaymentFiles"
            dataKey="id"
            @rowExpand="onExpand"
            :globalFilterFields="['status']"
            editMode="cell">
            <template #empty> No Bank Payment files found. </template>
            <Column expander style="width: 5rem" />
            <Column field="payment_type" header="Payment Type">
                <template #body="slotProps">
                    {{ formatPaymentType(slotProps.data.payment_type) }}
                </template>
            </Column>
            <Column sortable field="status" header="Status" filterField="status">
                <template #body="slotProps">
                    {{  statuses[slotProps.data.status].value }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText v-model="filterModel.value" type="text" @input="setStatusFilter" placeholder="Search by Status" />
                </template>
                <template #editor="{ data, field }">
                    <Select v-model="data[field]" :options="statusOptions" optionLabel="name" optionValue="code" placeholder="Select Status" @update:modelValue="(value) => updateValue(data, field, value)" />
                </template>
            </Column>
            <Column field="sum" header="Sum">
                    <template #body="slotProps">
                        {{ formatCurrency(slotProps.data.sum) }}
                    </template>
            </Column>
            <Column>
                <template #body="slotProps">
                  <Button @click="downloadPaymentFile(slotProps.data.id, false)" class="mr-2">
                    <span class="pi pi-download"/>
                    Download
                  </Button>
                  <Button @click="downloadPaymentFile(slotProps.data.id, true)" severity="secondary" class="mr-2">
                    <span class="pi pi-eye"/>
                    Preview
                  </Button>
                  <Button
                    @click="confirmPushToBank(slotProps.data)"
                    :disabled="slotProps.data.status !== 'UNPROCESSED'"
                    :loading="pushInProgress[slotProps.data.id]"
                    severity="danger">
                    <span class="pi pi-send"/>
                    Push to Bank
                  </Button>
                </template>
            </Column>
            <template #expansion="slotProps">
                <div>
                    <h5>Transaction Batch Count {{ transactionBatchesMap[slotProps.data.id] ? transactionBatchesMap[slotProps.data.id].length : 0 }}</h5>
                    <TransactionBatchView size="small" :transactionBatches="transactionBatchesMap[slotProps.data.id]" />
                </div>
            </template>
        </DataTable>

        <!-- Preview Dialog -->
        <Dialog v-model:visible="previewVisible" modal header="Payment File Preview" style="width: 80vw" :maximizable="true">
            <DataTable :value="previewData" scrollable scrollHeight="400px" class="mt-3">
                <Column v-for="col in previewColumns" :key="col.field" :field="col.field" :header="col.header"></Column>
            </DataTable>
        </Dialog>
    </div>
</template>

